import { memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { Icons, IconType } from '../../../../constants/icons';
import { Images } from '../../../../constants/images';
import { AnswerCardStatus } from '../../../Organisms';
import { CharacterPreview } from '../CharacterPreview';
import { Character } from '../models';

import styles from './HandwritingAnswerCard.module.css';

export type HandwritingAnswerCardData = {
  strokeIndexes: number[];
  status: AnswerCardStatus;
  color?: string;
};

type HandwritingAnswerCardProps = {
  size?: number;
  character: Character;
  card: HandwritingAnswerCardData;
  tutorialCard?: HandwritingAnswerCardData;
  onClick: () => void;
  onUpdateCardStatus: (
    card: HandwritingAnswerCardData,
    newStatus: AnswerCardStatus,
  ) => void;
};

export const HandwritingAnswerCard = memo(
  (props: HandwritingAnswerCardProps) => {
    const {
      size = 82,
      character,
      card,
      tutorialCard,
      onClick,
      onUpdateCardStatus,
    } = props;

    const containerVariants = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.8 },
    };

    const emptyVariants = {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    };

    return (
      <AnimatePresence>
        {card.status === AnswerCardStatus.HintCrossed && (
          <motion.div
            className={`${styles.container} ${styles.hintEliminate}`}
            style={{ width: size, height: size }}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <CharacterPreview
              character={character}
              size={size}
              borderRadius={'0.5rem'}
              getColor={(index) =>
                card.strokeIndexes.includes(index)
                  ? card.color ?? 'black'
                  : undefined
              }
            />
            {/* <SvgIcon name={SVGIconType.RedCross} className={styles.redCross} /> */}
            <img
              src={Icons[IconType.CrossWithOutline]}
              className={`${styles.redCross} ${styles.centerIconPos}`}
            />
          </motion.div>
        )}

        {card.status === AnswerCardStatus.Pending && (
          <motion.div
            className={`${styles.container} ${styles.pending} ${
              card === tutorialCard ? styles.cardWithOverlay : ''
            }`}
            style={{ width: size, height: size }}
            whileTap={{ scale: 0.92 }}
            onClick={onClick}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <CharacterPreview
              character={character}
              size={size}
              borderRadius={'0.5rem'}
              getColor={(index) =>
                card.strokeIndexes.includes(index)
                  ? card.color ?? 'black'
                  : undefined
              }
            />
            {card === tutorialCard &&
              card.status === AnswerCardStatus.Pending && (
                <motion.img
                  src={Images.HandwriteFinger}
                  alt="Finger pointing"
                  className={styles.fingerOverlay}
                  initial={{ x: 10, y: 10 }}
                  animate={{ x: [4, -4], y: [4, -4] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    repeatType: 'reverse',
                  }}
                  style={{ pointerEvents: 'none' }}
                />
              )}
          </motion.div>
        )}

        {card.status === AnswerCardStatus.Correct && (
          <motion.div
            className={`${styles.container} ${styles.correct}`}
            style={{ width: size, height: size }}
            whileTap={{ scale: 0.92 }}
            onClick={onClick}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Empty);
              }, 600); // Wait before updating status
            }}
          >
            <CharacterPreview
              character={character}
              size={size}
              borderRadius={'0.5rem'}
              getColor={(index) =>
                card.strokeIndexes.includes(index)
                  ? card.color ?? 'black'
                  : undefined
              }
            />
            <img
              src={Icons[IconType.TickWithOutline]}
              className={styles.tick}
            />
          </motion.div>
        )}

        {card.status === AnswerCardStatus.Wrong && (
          <motion.div
            className={`${styles.container} ${styles.wrong}`}
            style={{ width: size, height: size }}
            whileTap={{ scale: 0.92 }}
            onClick={onClick}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Pending);
              }, 600); // Wait before updating status
            }}
          >
            <CharacterPreview
              character={character}
              size={size}
              borderRadius={'0.5rem'}
              getColor={(index) =>
                card.strokeIndexes.includes(index)
                  ? card.color ?? 'black'
                  : undefined
              }
            />
            <img
              src={Icons[IconType.CrossWithOutline]}
              className={styles.redCross}
            />
          </motion.div>
        )}

        {card.status === AnswerCardStatus.Empty && (
          <motion.div
            className={`${styles.container} ${styles.empty}`}
            style={{ width: size, height: size }}
            variants={emptyVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
          />
        )}
      </AnimatePresence>
    );
  },
);
